import { useLayoutEffect, useState } from 'react';
import type * as Y from 'yjs';
import { arrowSchema, boardMap, type Arrow } from '../../shared/doc.js';
import type { ProjectConn } from '../doc/useProject.js';
import type { Rect, View } from './geometry.js';

/** Where the segment from the centre of `r` towards (tx, ty) leaves the rectangle. */
function edgePoint(r: Rect, tx: number, ty: number) {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const dx = tx - cx;
  const dy = ty - cy;
  if (!dx && !dy) return { x: cx, y: cy };
  const s = Math.min(Math.abs(r.w / 2 / (dx || 1e-9)), Math.abs(r.h / 2 / (dy || 1e-9)));
  return { x: cx + dx * Math.min(1, s), y: cy + dy * Math.min(1, s) };
}

export function Arrows({ conn, boardId, view, world, version, selected, onSelect }: {
  conn: ProjectConn;
  boardId: string;
  view: View;
  world: HTMLDivElement | null;
  version: number;
  selected: string | null;
  onSelect: (arrowId: string) => void;
}) {
  const [rects, setRects] = useState<Map<string, Rect>>(new Map());
  // Unknown or malformed arrow data (a future version, or a buggy agent) is skipped rather than
  // drawn or dereferenced — the rest of the board must keep rendering.
  const arrows = [...((boardMap(conn.doc, boardId)?.get('arrows') as Y.Map<unknown> | undefined)?.entries() ?? [])].filter(
    (entry): entry is [string, Arrow] => arrowSchema.safeParse(entry[1]).success,
  );

  useLayoutEffect(() => {
    if (!world) return;
    const cards = [...world.querySelectorAll<HTMLElement>('[data-card-id]')];
    const measure = () => {
      const base = world.getBoundingClientRect();
      const next = new Map<string, Rect>();
      for (const el of cards) {
        const r = el.getBoundingClientRect();
        next.set(el.dataset.cardId!, { x: (r.left - base.left) / view.zoom, y: (r.top - base.top) / view.zoom, w: r.width / view.zoom, h: r.height / view.zoom });
      }
      setRects(next);
    };
    measure();
    // A card's own content can resize it after this mount (typing, todo items, an image or
    // preview loading) without the board-level `version` changing (card content lives in the
    // 'cards' map, not in this board's), so watch the same elements directly rather than only
    // re-measuring on view/version changes — otherwise an arrow would keep pointing at a stale,
    // pre-resize edge. view.ox/oy: panning culls and un-culls cards, which must be measured once
    // they mount too.
    const ro = new ResizeObserver(measure);
    for (const el of cards) ro.observe(el);
    return () => ro.disconnect();
  }, [world, version, view.ox, view.oy, view.zoom, arrows.length]);

  return (
    <svg className="arrows" aria-hidden={!arrows.length}>
      <defs>
        <marker id="arrowhead" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
        </marker>
      </defs>
      {arrows.map(([id, a]) => {
        const from = rects.get(a.from);
        const to = rects.get(a.to);
        if (!from || !to) return null;
        const p1 = edgePoint(from, to.x + to.w / 2, to.y + to.h / 2);
        const p2 = edgePoint(to, from.x + from.w / 2, from.y + from.h / 2);
        return (
          <g key={id} className={selected === id ? 'arrow selected' : 'arrow'} data-arrow-id={id} onPointerDown={(e) => { e.stopPropagation(); onSelect(id); }}>
            <line className="hit" x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} />
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} markerEnd="url(#arrowhead)" />
          </g>
        );
      })}
    </svg>
  );
}
